import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../utilities/http-utilities.service';
import { IEditWorkflow, ISaveWorkflow, IWorkflow, IWorkflowStepOption } from './workflows.interface';

export interface IWorkflowComponentState extends IBaseDatatableState<IWorkflow> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IWorkflow[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  selectedWorkflow: IWorkflow | null;
  selectedWorkflowLoading: ELoadStatus;
  singleCrudLoading: ELoadStatus;
  steps: IWorkflowStepOption[];
}

@Injectable()
export class WorkflowsStore extends BaseDatatableStore<IWorkflow, IWorkflowComponentState> {
  readonly steps$: Signal<IWorkflowStepOption[]> = this.selectSignal((state: IWorkflowComponentState) => state.steps);
  readonly selectedWorkflow$: Signal<IWorkflow | null> = this.selectSignal(
    (state: IWorkflowComponentState) => state.selectedWorkflow,
  );
  readonly selectedWorkflowStatus$: Observable<ELoadStatus> = this.select(
    (state: IWorkflowComponentState) => state.selectedWorkflowLoading,
  );

  readonly createOne = this.effect((trigger$: Observable<ISaveWorkflow>) =>
    trigger$.pipe(
      switchMap((item: ISaveWorkflow) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditWorkflow>) =>
    trigger$.pipe(
      switchMap((item: IEditWorkflow) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadSteps = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        this.getSteps().pipe(
          tapResponse(
            (response: IGetManyResponse<IWorkflowStepOption>) => this.patchState({ steps: response.data }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        ),
      ),
    ),
  );

  readonly loadOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ selectedWorkflowLoading: ELoadStatus.loading });

        return forkJoin({
          steps: this.getSteps(),
          workflow: this.getOneItem(id),
        }).pipe(
          tapResponse(
            ({ steps, workflow }) =>
              this.patchState({
                selectedWorkflow: workflow.data,
                selectedWorkflowLoading: ELoadStatus.success,
                steps: steps.data,
              }),
            (error) => {
              this.patchState({ selectedWorkflowLoading: ELoadStatus.failure });
              // eslint-disable-next-line no-console
              console.error('Error loading data', error);
            },
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IWorkflow>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      selectedWorkflow: null,
      selectedWorkflowLoading: ELoadStatus.initial,
      singleCrudLoading: ELoadStatus.initial,
      steps: [],
    });
  }

  private createItem(item: ISaveWorkflow): Observable<IBaseResponse<IWorkflow>> {
    return this.http.post<IBaseResponse<IWorkflow>>('workflows', item);
  }

  private updateItem(item: IEditWorkflow): Observable<IBaseResponse<IWorkflow>> {
    return this.http.patch<IBaseResponse<IWorkflow>>(`workflows/${item.id}`, item.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<IWorkflow>> {
    return this.http.delete<IBaseResponse<IWorkflow>>(`workflows/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IWorkflow>> {
    return this.http.get<IGetManyResponse<IWorkflow>>('workflows', { params });
  }

  private getOneItem(id: number): Observable<IBaseResponse<IWorkflow>> {
    const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['name', 'startWorkflowStepId'],
      join: ['workflowStepAssignments||workflowStepId,nextWorkflowStepId'],
    });

    return this.http.get<IBaseResponse<IWorkflow>>(`workflows/${id}`, { params });
  }

  private getSteps(): Observable<IGetManyResponse<IWorkflowStepOption>> {
    const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
      fields: ['name', 'statusCategory', 'isDefault'],
      limit: 1000,
      sort: [{ active: 'id', direction: 'asc' }],
    });

    return this.http.get<IGetManyResponse<IWorkflowStepOption>>('workflow-steps', { params });
  }
}
